import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { useParams } from 'react-router-dom'
import { usePIP } from './usePIP'
import { mountHls, prefersNativeHls } from '../utils/hlsEngine'
import {
  selectDisplayFinishEventCountdownstate,
  selectDisplayStartEventCountdownstate,
} from '../reducers/callSlice'
import {
  setAutoPlayUnlocker,
  selectAutoPlayUnlocker,
  clearAutoPlayUnmuteRequest,
  selectAudioPermissionGranted,
  setAudioPermissionGranted,
  setTencentConnectionDialog,
} from '../reducers/uiSlice'
import { selectCurrentStore } from '../reducers/storeSlice'
import { isDemoVideoStore, getDemoVideoUrl } from '../utils/demoVideoStores'

/**
 * Camino de reproducción por HLS.
 *
 * Es INDEPENDIENTE del camino WebRTC: no comparte estado, refs, constantes ni
 * condiciones con `useWebRtcPlayback`. Los dos nunca corren a la vez — la elección la
 * hace `Streaming` una sola vez, leyendo la configuración del cliente.
 */

/** Sin progreso en esta ventana se considera que la reproducción se detuvo. */
const HLS_STALL_TIMEOUT_MS = 10000

/**
 * A partir de qué distancia del borde se considera que el motor NO está reproduciendo el vivo
 * sino arrastrando una cola vieja, y hay que remontar aunque el video avance.
 *
 * Medido en navegador: al retomar el push después de un corte, el motor viejo carga la playlist
 * nueva y sigue reproduciendo lo que le quedaba en buffer, así que avanza —y por eso «parece»
 * sano— pero queda clavado 240 s detrás del borde. El espectador ve contenido viejo o un frame
 * congelado con la UI diciendo que todo está bien, y ese estado no se corrige solo: el
 * `recover()` del stall hace `startLoad()`, que no lo acerca al borde.
 *
 * El umbral es holgado a propósito, contra los escenarios benignos MEDIDOS: LL-HLS da 1,70 s,
 * el HLS clásico con preset Low 3,04 s, y el clásico sin preset 11-13 s. Poner 15 s dejaría al
 * último caso rozando el umbral y podría remontar un reproductor sano; 30 s es más del doble
 * del peor caso benigno y muy por debajo de los 240 s del caso roto.
 */
const HLS_STALE_LATENCY_SECONDS = 30

/** Cada cuánto se mira si el video avanza. */
const HLS_PROGRESS_CHECK_MS = 1000

/**
 * Sin progreso en esta ventana, habiendo habido imagen, se vuelve al spinner.
 *
 * Existe porque `streamingInProgress` no bajaba jamás una vez presentado el primer frame:
 * al cortar la señal sin finalizar el evento, el espectador quedaba mirando el último
 * frame congelado indefinidamente.
 *
 * Estaba en 60 s con el argumento de que los cortes del organizador medidos se recuperan
 * en ~30-78 s y ante la duda conviene esperar. Medido en producción el 28 ago 2026 (Chrome
 * real, corte limpio del push): el frame se congela AL SEGUNDO del corte —el buffer no
 * amortigua nada— así que esos 60 s eran un minuto entero de imagen quieta con la UI
 * declarando la transmisión activa. La espera no evitaba nada: la recuperación no depende
 * de este número, que sólo decide cuándo se le deja de mostrar una foto vieja al
 * espectador. Volver es gratis: el propio avance del video reactiva la imagen.
 *
 * El escalón caro no es el spinner sino el TEXTO «No hay una transmisión en curso», y ése
 * se retrasa aparte en `Streaming.jsx` para que siga apareciendo alrededor de los 90 s.
 */
const HLS_FROZEN_TIMEOUT_MS = 12000

/**
 * Ritmo del reintento de remonte. El techo es LO QUE DECIDE cuánto tarda en recuperarse: con
 * 30 s, la señal podía volver justo después de un intento y el siguiente llegaba medio minuto
 * más tarde. Medido: la recuperación tardaba ~30 s, casi toda espera del backoff.
 *
 * Bajar el techo a 8 s deja el peor caso en ~11 s (8 de espera + 3 del sondeo). El costo es un
 * GET de manifest cada 8 s por espectador afectado, y sólo mientras el stream está caído: son
 * ~1 KB contra una CDN, despreciable incluso con miles de espectadores. Sigue habiendo backoff
 * para no martillar en el primer segundo, sólo que se aplana antes.
 */
const HLS_RETRY_BASE_MS = 1500
const HLS_RETRY_MAX_MS = 8000

/**
 * Sin imagen en esta ventana Y con el manifest servible, la falla es de este espectador y
 * corresponde informarla. Si el manifest NO es servible no hay falla: el evento no
 * arrancó, y eso es el estado normal antes de la hora y entre pruebas.
 */
const HLS_STARTUP_TIMEOUT_MS = 15000

/**
 * Un manifest existe pero puede no ser reproducible: CSS empaqueta de forma perezosa y
 * durante unos segundos el archivo está sin segmentos. Se exige al menos un `#EXTINF`.
 */
/**
 * Cuánto se espera al sondeo antes de darlo por fallido. **Sin esto el reintento muere.**
 * Medido: al cortar el push este CDN no devuelve 404, **cuelga**. Un `fetch` sin timeout se
 * queda esperando indefinidamente, el callback del reintento nunca termina y nunca se
 * reprograma, así que el remonte no vuelve a intentarse nunca más.
 */
const MANIFEST_PROBE_TIMEOUT_MS = 3000

const manifestIsServible = async (url) => {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), MANIFEST_PROBE_TIMEOUT_MS)
  try {
    const response = await fetch(url, { cache: 'no-store', signal: abort.signal })
    if (!response.ok) return false
    return /#EXTINF/.test(await response.text())
  } catch (_error) {
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Configuración de baja latencia. Sin esto el punto de sincronía de hls.js agrega ~6 s
 * sobre la profundidad del manifest: la latencia medida bajó de 11-13 s a 5-6 s.
 * Se puede apagar si aparecen cortes por falta de colchón.
 */
/**
 * Baja latencia sobre HLS clásico, contando SEGMENTOS. Bajó la latencia medida de 11-13 s a
 * 5-6 s sin tocar el CDN, y ése es el piso con segmentos de 2 s.
 */
const HLS_LOW_LATENCY_CONFIG = {
  liveSyncDurationCount: 1,
  liveMaxLatencyDurationCount: 4,
  maxBufferLength: 4,
  lowLatencyMode: true,
}

/**
 * Baja latencia sobre LL-HLS, contando SEGUNDOS.
 *
 * Con *partial segments* el conteo de segmentos deja de servir: el punto de sincronía tiene
 * que expresarse en tiempo, no en unidades cuya duración decide el CDN. `liveSyncDuration`
 * pisa a `liveSyncDurationCount` cuando está presente, así que no se mezclan.
 *
 * `maxLiveSyncPlaybackRate` deja que el player acelere apenas para recuperar el borde en vez
 * de saltar, que es lo que produce el negro corto al reengancharse.
 */
const HLS_ULTRA_LOW_LATENCY_CONFIG = {
  lowLatencyMode: true,
  liveSyncDuration: 1.5,
  liveMaxLatencyDuration: 6,
  maxBufferLength: 4,
  maxLiveSyncPlaybackRate: 1.5,
}

/**
 * Políticas de reintento del manifest. Los defaults de hls.js son inservibles para un evento
 * cuyo organizador corta y retoma: `manifestLoadPolicy.errorRetry.maxNumRetry` es **1**, el
 * recurso con menos tolerancia de todo el player, y `playlistLoadPolicy` tolera 2 timeouts.
 * Agotados esos, el error escala a fatal y la reproducción no vuelve.
 *
 * Medido contra este CDN: al cortar el push el manifest **cuelga** (timeout), no devuelve
 * 404, y **no escribe `EXT-X-ENDLIST`**. Las dos cosas importan:
 *   - El timeout SÍ lo reintenta hls.js, así que alcanza con no limitar los reintentos.
 *   - Sin `ENDLIST`, la playlist no queda marcada como terminada. Si algún día el CDN
 *     empezara a escribirlo en un corte temporal, hls.js pondría `live = false`, dejaría de
 *     recargar la playlist para siempre y ningún reintento lo arreglaría: haría falta
 *     rehacer el ciclo de vida del motor.
 *
 * `shouldRetry` además fuerza el reintento en 404 y 403, que hls.js descarta por diseño
 * ("such error cannot be recovered, retrying is useless") — cierto para un VOD, falso para
 * un evento en vivo que todavía no arrancó o que está entre pruebas.
 */
const HLS_FOREVER_RETRY = {
  maxNumRetry: Infinity,
  retryDelayMs: 2000,
  maxRetryDelayMs: 15000,
  backoff: 'linear',
  shouldRetry: (_config, _retryCount, _isTimeout, loaderResponse, retry) => {
    const status = loaderResponse?.code
    if (status === 404 || status === 403) return true
    return retry
  },
}

/**
 * Cuánto se le da a cada intento antes de considerarlo fallido.
 *
 * Los defaults de hls.js son 10 s al primer byte y 20 s de carga total. Con el manifest de
 * este CDN eso es fatal: al cortar el push **cuelga** en vez de dar 404, así que cada
 * reintento consume el timeout completo. Reintentos infinitos con timeouts de 20 s son
 * infinitamente lentos — medido: con la señal de vuelta 78 s antes, no llegó a recuperar.
 *
 * Con LL-HLS es todavía más marcado: los *partial segments* son de cientos de milisegundos,
 * así que un intento que tarda 20 s no tiene ninguna relación con la escala del contenido.
 */
const HLS_LOAD_TIMEOUT_MS = 4000
const HLS_FIRST_BYTE_TIMEOUT_MS = 2500

const HLS_RETRY_CONFIG = {
  manifestLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: HLS_FIRST_BYTE_TIMEOUT_MS,
      maxLoadTimeMs: HLS_LOAD_TIMEOUT_MS,
      timeoutRetry: { ...HLS_FOREVER_RETRY },
      errorRetry: { ...HLS_FOREVER_RETRY },
    },
  },
  playlistLoadPolicy: {
    default: {
      maxTimeToFirstByteMs: HLS_FIRST_BYTE_TIMEOUT_MS,
      maxLoadTimeMs: HLS_LOAD_TIMEOUT_MS,
      timeoutRetry: { ...HLS_FOREVER_RETRY },
      errorRetry: { ...HLS_FOREVER_RETRY },
    },
  },
  /**
   * Señal limpia de «el organizador cortó» sin inferirla de errores de red: la playlist
   * responde 200 pero deja de traer segmentos nuevos. Emite `PLAYLIST_UNCHANGED_ERROR` no
   * fatal. El default es `Infinity`, o sea deshabilitado.
   */
  liveMaxUnchangedPlaylistRefresh: 10,
}

const isLowLatencyEnabled = () => process.env.REACT_APP_TENCENT_HLS_LOW_LATENCY !== 'false'

/**
 * Con LL-HLS hay que contar en segundos y no en segmentos; con HLS clásico, al revés.
 * Aplicar la config equivocada deja la latencia peor que sin configurar nada.
 */
export const resolveLatencyConfig = () => {
  if (!isLowLatencyEnabled()) return {}
  return isLowLatencyPackage() ? HLS_ULTRA_LOW_LATENCY_CONFIG : HLS_LOW_LATENCY_CONFIG
}

const HLS_LOGS_ENABLED =
  process.env.NODE_ENV !== 'production' || process.env.REACT_APP_TENCENT_PLAYER_LOGS === 'true'

const hlsLog = (...args) => {
  if (HLS_LOGS_ENABLED) console.debug('[HLS]', ...args)
}

/**
 * Diagnóstico observable en producción, igual que en el camino WebRTC: permite leer la
 * latencia real y el motor activo en el navegador de un espectador sin banderas de build.
 */
const publishHlsDiagnostics = (data) => {
  if (typeof window === 'undefined') return
  window.__gjfHlsDiag = { ...(window.__gjfHlsDiag || {}), ...data }
}

/**
 * Traza acotada de lo que va pasando en el camino. Sin esto no se puede distinguir "el
 * remonte se intentó y falló" de "el remonte no se intentó", que es justo la diferencia que
 * hace falta para diagnosticar un corte a mitad de evento.
 */
const HLS_TRACE_MAX = 40

/**
 * Los errores del motor se repiten muchas veces por segundo durante un corte y llenaban la
 * traza, tapando los eventos propios —que son los que dicen si el remonte se está
 * intentando—. Los repetidos se cuentan en lugar de acumularse.
 */
const traceHls = (evento, detalle) => {
  if (typeof window === 'undefined') return
  const diag = window.__gjfHlsDiag || {}
  const traza = diag.trace || []
  const t = Math.round(performance.now() / 1000)
  const ultimo = traza[traza.length - 1]

  if (ultimo && ultimo.e === evento && ultimo.d === detalle) {
    ultimo.n = (ultimo.n || 1) + 1
    ultimo.hasta = t
  } else if (traza.length < HLS_TRACE_MAX) {
    traza.push({ t, e: evento, d: detalle })
  } else {
    // Con la traza llena se conserva el primer tramo y se sobreescribe el último, para no
    // perder el arranque ni el estado actual.
    traza[HLS_TRACE_MAX - 1] = { t, e: evento, d: detalle }
  }
  window.__gjfHlsDiag = { ...diag, trace: traza }
}

/**
 * Este camino tiene su propia variable de entorno. Se usa el manifest PLANO: la escalera
 * ABR de este CDN no está aprovisionada, y el master de variantes se sirve igual aunque
 * ninguna variante exista.
 *
 * DEUDA TRANSITORIA: mientras `REACT_APP_TENCENT_HLS_PLAYBACK_URL` no esté aprovisionada
 * en los entornos, se deriva de la URL de WebRTC cambiándole el esquema. Es el único
 * punto donde este camino mira una variable del otro, y hay que quitarlo en cuanto la
 * variable propia exista.
 */
/**
 * Protocolo de empaquetado que se le pide al CDN. Vacío = HLS-TS clásico, el de siempre.
 *
 * `ll-hls-ts` es LL-HLS real con *partial segments*: el CDN entrega trozos de 200-1000 ms
 * en lugar de esperar el segmento completo, y la latencia medida baja de ~3 s a ~1,7 s.
 * Requiere activarlo en la consola de CSS —Domain Management, dominio de playback, Live
 * Stream Package— y se factura aparte, 0,005 USD/min por protocolo empaquetado.
 *
 * OJO, medido en iPhone real: el player nativo de iOS NO reproduce el LL-HLS-TS de Tencent
 * (el manifest abre con ícono de video roto, mientras el clásico sí reproduce). La teoría
 * decía que al ser estándar de Apple el nativo lo iba a tragar; no es el caso con este
 * empaquetado: el LL-HLS de AVPlayer es con partials en fMP4/CMAF. Por eso este parámetro
 * se agrega sólo cuando el motor va a ser hls.js.
 */
const getHlsPackageType = () => process.env.REACT_APP_TENCENT_HLS_PACKAGE_TYPE || ''

export const isLowLatencyPackage = () => /^ll-hls/.test(getHlsPackageType())

/**
 * Empaquetado para el motor NATIVO, con su propia variable. Vacío = HLS-TS clásico, lo
 * único que el nativo reproduce con garantía medida.
 *
 * `ll-hls-cmaf` es la vía para baja latencia en iOS: AVPlayer sí habla LL-HLS, pero con
 * partials en CMAF. Requiere ANTES dos cosas en la consola de Tencent: activar el
 * protocolo en el dominio de playback (Domain Management → Live Stream Package) y
 * habilitar HTTPS con HTTP/2 — Tencent advierte que sin HTTP/2 el LL-HLS no reproduce
 * en Safari/iOS. Hasta medir eso en un iPhone real, el default sigue siendo la clásica.
 */
const getNativeHlsPackageType = () => process.env.REACT_APP_TENCENT_HLS_NATIVE_PACKAGE_TYPE || ''

export const buildHlsManifestUrl = (eventId, { nativo = false } = {}) => {
  if (!eventId) return undefined
  const propia = process.env.REACT_APP_TENCENT_HLS_PLAYBACK_URL
  const base = propia || process.env.REACT_APP_TENCENT_PLAYBACK_URL
  if (!base) return undefined
  const httpsBase = base.replace(/^webrtc:/, 'https:').replace(/\/$/, '')
  const url = `${httpsBase}/${eventId}.m3u8`
  const packageType = nativo ? getNativeHlsPackageType() : getHlsPackageType()
  return packageType ? `${url}?txPackageType=${packageType}` : url
}

export const useHlsPlayback = (setStreamingInProgress, streamingInProgress) => {
  const { eventId: roomId } = useParams()
  const { setCanPIP, setEnabled } = usePIP()
  const { postHogTrackEvent, postHogRegister } = usePostHogAnalytics()
  const dispatch = useDispatch()
  const displayFinishEventCountdown = useSelector(selectDisplayFinishEventCountdownstate)
  const displayStartEventCountdown = useSelector(selectDisplayStartEventCountdownstate)
  const autoPlayUnlocker = useSelector(selectAutoPlayUnlocker)
  const audioPermissionGranted = useSelector(selectAudioPermissionGranted)
  const store = useSelector(selectCurrentStore)

  const engineRef = useRef(null)

  /** El empaquetado del motor activo AHORA: separa iPhone (nativo, clásico) de PC/Android. */
  const paqueteActual = () =>
    engineRef.current?.engine === 'native' ? getNativeHlsPackageType() : getHlsPackageType()

  const audioPermissionGrantedRef = useRef(audioPermissionGranted)
  const hasPresentedFrameRef = useRef(false)
  const stalledReportedRef = useRef(false)
  /**
   * El aviso «hay señal y este espectador no puede verla» se da UNA vez, igual que en el
   * camino WebRTC (`dialogShownRef`): el watchdog de arranque se rearma en cada remonte,
   * así que en el escenario «manifest servible pero nunca hay frame» (decoder roto, codec
   * no soportado) el bucle de remontes re-disparaba el cartel y la métrica cada ~30 s por
   * espectador, para siempre — y reabría el modal aunque el espectador lo cerrara. Se
   * levanta cuando llega el primer frame, que es cuando el diagnóstico deja de ser cierto.
   */
  const unreachableReportedRef = useRef(false)
  /**
   * Momento del último avance de la reproducción. Es lo que decide si hay que remontar, y no
   * `hasPresentedFrameRef`: haber tenido imagen alguna vez no dice nada sobre AHORA.
   */
  const lastAdvanceAtRef = useRef(0)
  /** Última distancia al borde conocida. `null` cuando el motor no la sabe calcular. */
  const latencyRef = useRef(null)
  /**
   * Se detectó que el motor arrastra contenido viejo y hay que remontar pase lo que pase. No se
   * baja por «parece que reproduce»: sólo cuando el remonte efectivamente ocurre.
   */
  const remonteForzadoRef = useRef(false)

  useEffect(() => {
    if (!roomId) return

    // Las tiendas demo reproducen un VOD en lugar de la señal. Es un `.m3u8`, así que lo
    // toma el mismo motor; lo único distinto es que se repite, por ser contenido finito.
    const esDemo = isDemoVideoStore(store)
    const url = esDemo
      ? getDemoVideoUrl()
      : buildHlsManifestUrl(roomId, { nativo: prefersNativeHls() })
    const videoElement = document.getElementById('hls-video')
    if (videoElement) videoElement.loop = esDemo
    if (!url || !videoElement) {
      hlsLog('sin URL o sin elemento de video: no se monta')
      return
    }

    let cancelled = false
    let progressIntervalId = null
    let retryTimer = null
    let remountEnCurso = false
    let retryDelay = HLS_RETRY_BASE_MS
    let startupTimer = null

    /**
     * Igual que en el camino WebRTC, la transmisión se declara activa con imagen real y
     * no con un evento de disponibilidad. La implementación es propia de este camino.
     */
    const watchProgress = () => {
      let lastTime = videoElement.currentTime
      let lastFrames = videoElement.getVideoPlaybackQuality?.().totalVideoFrames ?? null
      let lastAdvanceAt = Date.now()
      lastAdvanceAtRef.current = lastAdvanceAt

      progressIntervalId = setInterval(() => {
        if (cancelled) return
        // Con la pestaña oculta el navegador deja de decodificar: `currentTime` no avanza
        // y sin esta guarda se declararía un stall falso, que además dispara una
        // recuperación estando oculto y termina en pantalla negra al volver.
        if (document.visibilityState !== 'visible') {
          lastAdvanceAt = Date.now()
          return
        }
        const frames = videoElement.getVideoPlaybackQuality?.().totalVideoFrames ?? null
        const relojAvanzo = videoElement.currentTime > lastTime

        /**
         * El avance se mide en FRAMES PRESENTADOS, no en el reloj. Medido: al volver la señal
         * el motor viejo resincroniza su reloj al borde —la latencia baja a 4-5 s y
         * `currentTime` pega un salto— pero el decodificador queda muerto en `readyState: 1`
         * sin presentar un solo frame nuevo. Mirando el reloj eso pasaba por «vivo» y cancelaba
         * el remonte que era justamente lo que lo salvaba. Los frames son la única señal que no
         * miente, y es la misma con la que ya se declara el primer frame.
         *
         * Si el navegador no expone el contador se cae al reloj, que es lo único disponible.
         */
        const advanced = frames === null ? relojAvanzo : frames > lastFrames

        if (!hasPresentedFrameRef.current && ((frames !== null && frames > 0) || relojAvanzo)) {
          hasPresentedFrameRef.current = true
          unreachableReportedRef.current = false
          hlsLog('primer frame presentado')
          if (startupTimer) {
            clearTimeout(startupTimer)
            startupTimer = null
          }
          // Si el cartel se había abierto, ahora hay imagen: dejó de ser cierto.
          dispatch(setTencentConnectionDialog({ open: false }))
          setStreamingInProgress(true)
        }

        // La distancia al borde es LA medida de si la baja latencia sirvió de algo, y además
        // lo único que distingue «reproduciendo el vivo» de «arrastrando la cola vieja».
        const latency = engineRef.current?.getLatency?.()
        latencyRef.current = typeof latency === 'number' ? latency : null
        if (typeof latency === 'number') {
          publishHlsDiagnostics({ latencySeconds: Number(latency.toFixed(2)) })
        }

        if (advanced) {
          lastTime = videoElement.currentTime
          lastFrames = frames
          lastAdvanceAt = Date.now()
          lastAdvanceAtRef.current = lastAdvanceAt
          stalledReportedRef.current = false
          // Avanzar reactiva la imagen si estaba el spinner por la ventana de freeze.
          setStreamingInProgress(true)

          // Avanzar NO alcanza para dar por sana la reproducción: puede estar consumiendo lo
          // que quedó en buffer, lejísimos del borde. Ese estado necesita remonte, y salir con
          // `return` acá era lo que lo dejaba clavado para siempre.
          if (latenciaRancia()) {
            traceHls('latencia_rancia', `${Math.round(latencyRef.current)}s`)
            // Irrevocable a propósito: detectada la cola vieja, se remonta sí o sí. El motor
            // puede volver a «parecer» sano —resincronizando el reloj, bajando la latencia— y
            // cancelar el remonte que lo salvaba. Ese skip dejaba el loop entero mudo.
            remonteForzadoRef.current = true
            scheduleRemount()
          }
          return
        }

        if (hasPresentedFrameRef.current && Date.now() - lastAdvanceAt > HLS_STALL_TIMEOUT_MS) {
          // `scheduleRemount` se pide en CADA tick del stall, no una sola vez: es idempotente
          // porque sale si ya hay un reintento en vuelo. Antes se pedía una vez y si ese
          // intento no prosperaba, el watchdog no volvía a insistir nunca.
          scheduleRemount()

          // La telemetría y el `recover()` del motor sí van una sola vez por episodio.
          if (!stalledReportedRef.current) {
            stalledReportedRef.current = true
            traceHls('stalled', `sin progreso ${Date.now() - lastAdvanceAt}ms`)
            hlsLog('la reproducción se detuvo')
            postHogTrackEvent('live-hls-stalled', {
              eventId: roomId,
              companyId: store?.company?.id,
              engine: engineRef.current?.engine,
              packageType: paqueteActual() || 'hls-ts (clásico)',
              latencySeconds:
                typeof latencyRef.current === 'number'
                  ? Number(latencyRef.current.toFixed(2))
                  : undefined,
            })
            // `recover()` hace `startLoad()`, que reengancha la carga pero NO acerca al borde.
            // Medido: con el motor clavado lejos del borde es un no-op, así que ahí sólo sirve
            // el remonte, que ya quedó pedido arriba.
            if (!latenciaRancia()) engineRef.current?.recover()
          }

          // Superada la ventana de freeze, el último frame no se queda quieto para
          // siempre: vuelve el spinner hasta que la señal retorne.
          if (Date.now() - lastAdvanceAt > HLS_FROZEN_TIMEOUT_MS) {
            traceHls('frozen', `sin progreso ${Math.round((Date.now() - lastAdvanceAt) / 1000)}s`)
            setStreamingInProgress(false)
          }
        }
      }, HLS_PROGRESS_CHECK_MS)
    }

    /** Vuelve a montar el motor cuando reaparece señal. Nunca se rinde. */
    /**
     * ¿Está tan lejos del borde que lo que reproduce ya no es el vivo? Si el motor no sabe
     * calcular la distancia se responde que no: sin el dato no se puede afirmar que esté rancio,
     * y el watchdog de stall sigue cubriendo el caso de video detenido.
     *
     * Sólo aplica al motor hls.js: el umbral se calibró con SUS mediciones (ver la constante).
     * El nativo de iOS se posiciona solo, 20-40 s detrás del borde, y ése es su estado SANO:
     * aplicarle el umbral fuerza un remonte apenas vuelve la imagen, y otro a los pocos
     * segundos — el espectador ve parpadeo entre video y pantalla negra en bucle. La cola
     * vieja de 240 s que esta guarda persigue fue medida en hls.js tras un corte de push;
     * en el nativo el video detenido lo cubren su watchdog propio y el de stall.
     */
    const latenciaRancia = () =>
      engineRef.current?.engine === 'hlsjs' &&
      typeof latencyRef.current === 'number' &&
      latencyRef.current > HLS_STALE_LATENCY_SECONDS

    /**
     * ¿Está reproduciendo EL VIVO ahora? Avanzar no basta: un motor que arrastra la cola vieja
     * avanza igual. Preguntar sólo por el avance dejaba el remonte cancelado justo cuando la
     * señal volvía, que es el momento en que hace falta.
     */
    const reproduciendoElVivo = () =>
      !remonteForzadoRef.current &&
      lastAdvanceAtRef.current > 0 &&
      Date.now() - lastAdvanceAtRef.current < HLS_STALL_TIMEOUT_MS &&
      !latenciaRancia()

    const scheduleRemount = () => {
      // NO se mira si alguna vez hubo imagen: en un corte a mitad de evento ese flag ya está
      // en true, y mirarlo dejaba el remonte inalcanzable justo cuando más hace falta. Es el
      // mismo patrón de estado absorbente que este producto ya tuvo dos veces.
      //
      // `remountEnCurso` cubre TODO el intento, incluidos sus `await`. El timer solo no
      // alcanza: se pone en null al disparar y después el intento espera el sondeo y el
      // montaje, así que el watchdog —que insiste cada segundo— colaba un segundo intento en
      // esa ventana y el `destroy()` del segundo mataba el motor que el primero acababa de
      // montar, antes de que presentara un frame.
      if (cancelled || retryTimer || remountEnCurso || esDemo) return
      if (reproduciendoElVivo()) {
        traceHls('remount_skip', 'reproduciendo el vivo')
        return
      }
      retryTimer = setTimeout(async () => {
        retryTimer = null
        if (cancelled) return
        remountEnCurso = true
        let reprogramar = false
        try {
          reprogramar = await intentarRemonte()
        } finally {
          remountEnCurso = false
        }
        // La reprogramación va DESPUÉS de bajar la bandera: pedirla desde dentro del intento
        // no hacía nada, porque la bandera propia bloqueaba el reintento siguiente.
        if (reprogramar) scheduleRemount()
      }, retryDelay)
    }

    /**
     * Un único intento de remonte. Corre con `remountEnCurso` en alto de punta a punta, así
     * que NO puede reprogramarse a sí mismo: devuelve si hay que volver a intentar.
     */
    const intentarRemonte = async () => {
      // Si volvió a reproducir no hay nada que remontar, pero TAMPOCO se abandona: se
      // vuelve a programar. Salir sin reprogramar dejaba el reintento muerto para siempre.
      if (reproduciendoElVivo()) {
        traceHls('remount_skip', 'reproduciendo el vivo')
        return true
      }

      // Con la pestaña oculta no se remonta —no hay nada que mostrar y el navegador no
      // compone— pero se sigue programando para cuando vuelva.
      const listo = document.visibilityState === 'visible' && (await manifestIsServible(url))
      if (!listo) {
        traceHls('remount_wait', `delay=${retryDelay}`)
        retryDelay = Math.min(retryDelay * 2, HLS_RETRY_MAX_MS)
        return true
      }

      traceHls('remount_go')
      hlsLog('reapareció señal: se reinicia el motor')
      engineRef.current?.destroy()
      engineRef.current = null
      if (progressIntervalId) clearInterval(progressIntervalId)
      progressIntervalId = null
      lastAdvanceAtRef.current = 0
      stalledReportedRef.current = false
      // El remonte ocurrió: acá y SÓLO acá se saldan las dos deudas que lo pidieron. Dejarlas
      // puestas convierte «remontar sí o sí» en «remontar para siempre», un remonte por tick.
      // La latencia vieja es del motor que se acaba de destruir; el nuevo publicará la suya.
      remonteForzadoRef.current = false
      latencyRef.current = null
      retryDelay = HLS_RETRY_BASE_MS
      try {
        await mount()
      } catch (error) {
        // Si el montaje falla no queda watcher, así que nadie detectaría el stall: hay que
        // volver a intentar o el camino muere en silencio.
        traceHls('remount_failed', error?.message)
        return true
      }
      return false
    }

    /**
     * El cartel informa, no rescata: no conmuta de protocolo ni recarga. Sólo se muestra
     * si el manifest ES servible, o sea si hay alguien transmitiendo y aun así este
     * espectador no puede ver.
     */
    const armStartupWatchdog = () => {
      // El demo es un VOD: si no reproduce no es por falta de señal en vivo, y sondear el
      // manifest del evento no dice nada sobre él.
      if (esDemo || startupTimer) return
      startupTimer = setTimeout(async () => {
        startupTimer = null
        if (cancelled || hasPresentedFrameRef.current) return
        if (!(await manifestIsServible(url))) {
          hlsLog('sin imagen pero tampoco hay señal: no es falla, se espera')
          return
        }
        if (cancelled || hasPresentedFrameRef.current) return
        // Una sola vez: este watchdog se rearma en cada remonte, y en este escenario el
        // remonte es en bucle. El aviso y la métrica no se repiten (ver la declaración del ref).
        if (unreachableReportedRef.current) return
        unreachableReportedRef.current = true

        hlsLog('hay señal y este espectador no puede verla: se informa')
        postHogTrackEvent('live-hls-unreachable', {
          eventId: roomId,
          companyId: store?.company?.id,
          engine: engineRef.current?.engine,
          packageType: paqueteActual() || 'hls-ts (clásico)',
        })
        dispatch(
          setTencentConnectionDialog({
            open: true,
            errorMessage: 'hls_no_frames_with_servible_manifest',
          }),
        )
      }, HLS_STARTUP_TIMEOUT_MS)
    }

    const mount = async () => {
      hlsLog('montando', url)
      const engine = await mountHls({
        videoElement,
        url,
        config: {
          ...HLS_RETRY_CONFIG,
          ...resolveLatencyConfig(),
        },
        onEvent: (name, detail) => {
          hlsLog(name, detail)
          traceHls(name, detail?.details || detail?.type || detail?.engine || detail?.name)
          if (name === 'engine_dead') {
            postHogTrackEvent('live-hls-engine-dead', {
              eventId: roomId,
              companyId: store?.company?.id,
              details: detail?.details,
              engine: engine.engine,
              packageType: paqueteActual() || 'hls-ts (clásico)',
            })
          }
          // Un motor muerto o que no cargó no puede ser el final del camino.
          if (name === 'engine_dead' || name === 'hlsjs_load_failed') scheduleRemount()
        },
      })

      if (cancelled) {
        engine.destroy()
        return
      }
      engineRef.current = engine
      hlsLog('motor', engine.engine)
      const paqueteEfectivo =
        engine.engine === 'native' ? getNativeHlsPackageType() : getHlsPackageType()
      publishHlsDiagnostics({
        engine: engine.engine,
        packageType: paqueteEfectivo || 'hls-ts (clásico)',
        lowLatency: /^ll-hls/.test(paqueteEfectivo),
        url,
      })
      // Una vez por sesión de reproducción, igual que en el camino WebRTC.
      postHogRegister({
        playbackProtocol: 'hls',
        playbackEngine: engine.engine,
        hlsPackageType: paqueteEfectivo || 'hls-ts (clásico)',
      })

      // El audio arranca muteado por la política de autoplay. Si el espectador ya dio
      // permiso en esta sesión se desmutea; si no, se le pide el gesto.
      if (audioPermissionGrantedRef.current) {
        /**
         * Desmutear SIN gesto del usuario hace que Safari PAUSE el video, y a partir de
         * ahí todo `play()` se rechaza: el espectador queda sin imagen con el motor sano.
         * Medido en Safari de escritorio el 31-ago-2026 (`mounted hlsjs` y después dos
         * `play_rejected`, sin un solo error del motor). Es la misma trampa que ya estaba
         * cubierta en el desmuteo por gesto: si el desmuteo se rechaza, se vuelve a mutear
         * y se reproduce igual, y se le ofrece el gesto para habilitar el sonido.
         */
        videoElement.muted = false
        const reproducido = videoElement.play?.()
        if (reproducido?.catch) {
          reproducido.catch(() => {
            videoElement.muted = true
            videoElement.play?.()?.catch?.(() => {})
            dispatch(setAutoPlayUnlocker({ open: true }))
          })
        }
      } else {
        dispatch(setAutoPlayUnlocker({ open: true }))
      }

      watchProgress()
      armStartupWatchdog()
    }

    mount()

    const handleVcommMsg = (event) => {
      if (event.data?.type === 'MUTE_AUDIO') videoElement.muted = true
      if (event.data?.type === 'UNMUTE_AUDIO') videoElement.muted = false
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      engineRef.current?.recover()
    }

    window.addEventListener('message', handleVcommMsg)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      if (progressIntervalId) clearInterval(progressIntervalId)
      if (retryTimer) clearTimeout(retryTimer)
      if (startupTimer) clearTimeout(startupTimer)
      window.removeEventListener('message', handleVcommMsg)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      engineRef.current?.destroy()
      engineRef.current = null
      hasPresentedFrameRef.current = false
      stalledReportedRef.current = false
      unreachableReportedRef.current = false
      lastAdvanceAtRef.current = 0
      latencyRef.current = null
      remonteForzadoRef.current = false
      setStreamingInProgress(false)
    }
  }, [roomId])

  useEffect(() => {
    if (streamingInProgress && !displayFinishEventCountdown && !displayStartEventCountdown) {
      setCanPIP(true)
    } else {
      setEnabled(false)
      setCanPIP(false)
    }
  }, [streamingInProgress, displayFinishEventCountdown, displayStartEventCountdown])

  useEffect(() => {
    audioPermissionGrantedRef.current = audioPermissionGranted
  }, [audioPermissionGranted])

  useEffect(() => {
    if (!autoPlayUnlocker.requestUnmute) return
    const videoElement = document.getElementById('hls-video')
    try {
      if (videoElement) {
        videoElement.muted = false
        const played = videoElement.play?.()
        /**
         * El permiso se registra SOLO si el `play()` desmuteado se resolvió. Antes se ponía
         * en true de arranque y en el `finally`, sin mirar el resultado: el sistema quedaba
         * creyendo que el espectador tenía audio con el video muteado, y el siguiente mount
         * intentaba desmutear sin gesto, se lo rechazaban y volvía a pedir el gesto. Eso es
         * el "tuve que tocar dos veces, las dos pegadas" reportado el 31-ago-2026.
         *
         * Un desmuteo rechazado además tiene que volver a mutear: iOS detiene el video si la
         * promesa de play() se rechaza.
         */
        if (played?.then) {
          played
            .then(() => {
              audioPermissionGrantedRef.current = true
              dispatch(setAudioPermissionGranted(true))
            })
            .catch(() => {
              videoElement.muted = true
              videoElement.play?.()?.catch?.(() => {})
              audioPermissionGrantedRef.current = false
              dispatch(setAutoPlayUnlocker({ open: true }))
            })
        } else {
          // Navegador cuyo `play()` no devuelve promesa: no hay forma de saber el resultado.
          audioPermissionGrantedRef.current = true
          dispatch(setAudioPermissionGranted(true))
        }
      }
    } catch (error) {
      console.error('[HLS] error unmuting', error)
    } finally {
      dispatch(clearAutoPlayUnmuteRequest())
    }
  }, [autoPlayUnlocker.requestUnmute, dispatch])

  return null
}
